import { useMemo, useCallback, useState, Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Divider, Grid, Paper, Tab, Tabs, Typography, InputLabel } from '@mui/material';

import dayJs from 'dayjs';
import { appConfig } from '@app/app';
import { get } from '@app/app/utils/get';
import { useIsMobile } from '@app/app/hooks/useIsMobile';
import { Breadcrumbs } from '@app/ui/breadcrumbs';
import { Label } from '@app/ui/label';
import { Loader } from '@app/ui/loader';
import { useConfirmation, useModal } from '@app/ui/modal';
import { Dropdown } from '@app/ui/dropdown';
import { ApplicationSwitcher } from '@app/shared/components/ApplicationSwitcher';
import { error as showError, success as showSuccess } from '@app/snackbars';
import { USER_STATUS_COLOR, adminApplicationGeneralInfoItems } from '@app/user/constants';
import { USA_STATES_WITH_NOT_IN } from '@app/user/constants/usaStates';
import { isEditableStatus } from '@app/tenant';
import { useAuth } from '@app/auth';

import { ApplicationList } from './ApplicationList';
import { PriorityTagsCell } from './PriorityTagsCell';
import { ApplicationTabIcon, ProvenSurrogateIcon, WatchlistTabIcon } from './icons';
import { WatchlistTabLabel } from '../watchlist/WatchlistTabLabel';
import { WatchlistPanel } from '../watchlist/WatchlistPanel';
import { UploadForm } from '../upload/UploadForm';
import { ExportPdfDropdown } from '../pdf/ExportPdfDropdown';
import { UpdatePdfCoverModal } from '../pdf/UpdatePdfCoverModal';
import { Application } from '../../types/Application';
import { Watchlist } from '../../types/Watchlist';
import {
  applicationGetOne,
  APPLICATION_CREATE_NEW_JOURNEY,
  APPLICATION_LOCK_UPDATE,
  APPLICATION_UNLOCK_UPDATE,
  WATCHLIST_GET_ONE_BY_APPLICATION,
} from '../../gql';
import { applicationConfig } from '../../configs';
import { getFieldsFromSurveys } from '../../utils/survey';
import { useWatchlistViewed } from '../../hooks/useWatchlistViewed';

// `residency` stores the 2-letter id, not the display name — same constant the form's dropdown uses.
const getStateName = (value?: string): string =>
  USA_STATES_WITH_NOT_IN.find(state => state.id === value)?.name ?? value ?? '-';

export const AdminApplicationItem = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const routerParams = useParams();
  const isMobile = useIsMobile();
  const { isOpen, close, show } = useModal();
  const { entity, ...params } = useParams<{ id?: string; entity: 'surrogates' | 'parent' }>();

  const id = Number(params.id);

  const fields = useMemo(() => {
    const { surveyFields } = getFieldsFromSurveys(applicationConfig.surveys);

    // SAA-82/§6.3 — `isSurrogateBefore` and `priorityTags` are NOT survey elements, so
    // `getFieldsFromSurveys` never picks them up; each must be appended by hand or it arrives
    // `undefined` with no error (there is no codegen to catch it).
    // SAA-112 P-K — sibling applications feed the internal switcher (AC 3.1).
    return [
      ...surveyFields,
      'status',
      'user.email',
      'isSurrogateBefore',
      'priorityTags',
      'boxName',
      'isUnlocked',
      'user.applications.id',
      'user.applications.createdAt',
    ];
  }, []);

  const { data, loading, refetch } = useQuery<{ applicationGetOne?: Application }>(applicationGetOne(fields), {
    variables: { id },
    fetchPolicy: 'no-cache',
  });
  // AC 2.1.2 — the box name is the application's display name wherever one renders; first+last
  // is the fallback for rows whose box was never renamed/backfilled.
  const userName =
    data?.applicationGetOne?.boxName ||
    `${data?.applicationGetOne?.firstName || ''} ${data?.applicationGetOne?.lastName || ''}`.trim();

  // SAA-112 S3 — "New App": staff-initiated only, behind a confirmation (AC 1). Every `/admin/*`
  // route is already `isInternal`-gated at the router (`_private.tsx`) — no extra role check here.
  const [createNewJourney, { loading: isCreatingNewJourney }] = useMutation<{
    applicationCreateNewJourney: Application;
  }>(APPLICATION_CREATE_NEW_JOURNEY);

  const [confirmNewApp] = useConfirmation({
    title: t('application.button.newApp.modal.title'),
    submitButtonText: t('application.button.newApp.modal.confirm'),
    isLoading: isCreatingNewJourney,
  });

  const handleNewApp = useCallback(
    () =>
      confirmNewApp({
        description: t('application.button.newApp.modal.description', { name: userName }),
        onSubmit: async closeModal => {
          try {
            const { data: mutationData } = await createNewJourney({ variables: { input: { id } } });
            const newId = mutationData?.applicationCreateNewJourney?.id;

            closeModal();

            if (newId) {
              navigate(`/admin/${entity}/${newId}`);
            }
          } catch (e) {
            showError(t('general.error.somethingWentWrong'));
          }
        },
      }),
    [confirmNewApp, createNewJourney, entity, id, navigate, t, userName],
  );

  // SAA-128 — staff-only unlock/lock (AC-1/AC-5); absent from the menu on a still-editable first
  // application (AC-3, checked at the render site below).
  const [unlockApplication, { loading: isUnlockLoading }] = useMutation<{ applicationUnlockUpdate: Application }>(
    APPLICATION_UNLOCK_UPDATE,
  );
  const [lockApplication, { loading: isLockLoading }] = useMutation<{ applicationLockUpdate: Application }>(
    APPLICATION_LOCK_UPDATE,
  );

  const [confirmUnlockLock] = useConfirmation({ isLoading: isUnlockLoading || isLockLoading });

  const isUnlocked = data?.applicationGetOne?.isUnlocked;

  const handleUnlockLock = useCallback(() => {
    const key = isUnlocked ? 'lockProfile' : 'unlockProfile';
    const mutate = isUnlocked ? lockApplication : unlockApplication;

    confirmUnlockLock({
      title: t(`application.button.${key}.modal.title`),
      description: t(`application.button.${key}.modal.description`),
      submitButtonText: t(`application.button.${key}.modal.confirm`),
      onSubmit: async closeModal => {
        try {
          await mutate({ variables: { input: { id } } });

          closeModal();
          refetch();
          showSuccess(t(`application.button.${key}.successMessage`));
        } catch (e) {
          showError(t('general.error.somethingWentWrong'));
        }
      },
    });
  }, [confirmUnlockLock, id, isUnlocked, lockApplication, refetch, t, unlockApplication]);

  // SAA-112 P-K — switcher: sibling applications of the SAME user (AC 3.1/3.4). Numbers entries
  // by creation order ("Application 1", "Application 2", …); display order (below) is newest-first
  // and reversed from it. No "Latest" tag — removed deliberately in the design pass (D12/R7).
  const siblingApplicationsAsc = useMemo(
    () =>
      [...(data?.applicationGetOne?.user?.applications ?? [])]
        .filter((sibling): sibling is Application & { id: number } => sibling.id !== undefined)
        .sort((a, b) => +new Date(a.createdAt ?? 0) - +new Date(b.createdAt ?? 0)),
    [data?.applicationGetOne?.user?.applications],
  );

  const switcherOptions = useMemo(
    () =>
      siblingApplicationsAsc
        .map((sibling, index) => ({
          id: sibling.id,
          name: t('application.field.switcher.applicationName', { n: index + 1 }),
          meta: t('application.field.switcher.created', {
            date: dayJs(sibling.createdAt).format(appConfig.format.date),
          }),
        }))
        .reverse(),
    [siblingApplicationsAsc, t],
  );

  // SAA-91 — lives in the page, not the panel: the badge must render while the Application tab
  // is selected, so a query that only fires on panel mount would leave the badge empty until
  // someone clicks it. Panel and badge both take the result as a prop (no Apollo mock needed to
  // test either). `no-cache`, matching the sibling query above — a cached watchlist would mask a
  // regeneration.
  const {
    data: watchlistData,
    loading: watchlistLoading,
    error: watchlistError,
  } = useQuery<{ watchlistGetOneByApplication?: Watchlist | null }>(WATCHLIST_GET_ONE_BY_APPLICATION, {
    variables: { applicationId: id },
    fetchPolicy: 'no-cache',
  });
  const watchlist = watchlistData?.watchlistGetOneByApplication;

  // `react-hooks/rules-of-hooks` is OFF in this repo — kept unconditional at the top by hand.
  const [tab, setTab] = useState(0);
  const { user } = useAuth();
  // SAA-155 — badge hides once THIS user has opened the tab for THIS generation (AC 7.2).
  const watchlistViewed = useWatchlistViewed(user?.id, watchlist?.id, tab === 1);

  const breadcrumbs = useMemo(
    () => [
      {
        title: t(`user.page.adminList.title`),
        to: `/admin/${entity}`,
      },
      {
        title: userName,
      },
    ],
    [t, userName, entity],
  );

  const handleSurveyClick = useCallback(
    (survey: string) => navigate(`/admin/${routerParams.entity}/${id}/${survey}`),
    [id, routerParams],
  );

  return (
    <>
      <Breadcrumbs options={breadcrumbs} />
      <Box mb={3} display="flex" alignItems="center" flexWrap="wrap">
        <Typography variant="h1">{userName}</Typography>
        {data?.applicationGetOne?.status && (
          <Label
            sx={{ ml: 2, mt: 0.5, textTransform: 'capitalize' }}
            text={t(`application.field.status.option.${data?.applicationGetOne?.status}`)}
            color={USER_STATUS_COLOR[data?.applicationGetOne?.status]}
          />
        )}
        {/* Strict `=== true` — `false` and `undefined` both render nothing, not a greyed badge. */}
        {data?.applicationGetOne?.isSurrogateBefore === true && (
          <Box
            sx={{
              ml: 2,
              mt: 0.5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main',
              fontSize: 14,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <ProvenSurrogateIcon />
            {t('application.field.isSurrogateBefore.badge')}
          </Box>
        )}
        <Box sx={{ ml: 'auto', mt: 0.5 }}>
          <ApplicationSwitcher
            options={switcherOptions}
            value={id}
            onChange={newId => navigate(`/admin/${entity}/${newId}`)}
          />
        </Box>
      </Box>
      <Paper
        sx={{
          wordWrap: 'break-word',
          borderRadius: {
            md: '16px',
          },
          p: {
            xs: 2,
            sm: 3,
            lg: 5,
          },
          mb: 5,
        }}
        variant="outlined"
        elevation={0}
      >
        <Typography variant="h3" sx={{ mb: 3 }}>
          {t('user.page.single.information')}
        </Typography>

        {/* Cells size to content (`flexGrow` on an auto basis); a fixed `md={2}` plus the five
        Dividers overflows the row and wraps. */}
        <Grid container direction={isMobile ? 'column' : 'row'} wrap={isMobile ? 'wrap' : 'nowrap'}>
          {data?.applicationGetOne &&
            adminApplicationGeneralInfoItems.map((item, index) => {
              const name = item.split('.').pop();
              const lastChild = index === adminApplicationGeneralInfoItems.length - 1;

              let value: ReactNode;

              if (item === 'priorityTags') {
                value = <PriorityTagsCell tags={data.applicationGetOne?.priorityTags} />;
              } else if (item === 'residency') {
                value = getStateName(get(data.applicationGetOne || {}, item));
              } else {
                value = get(data.applicationGetOne || {}, item) || '-';
              }

              return (
                <Fragment key={item}>
                  <Grid item sx={{ flexGrow: 1, minWidth: 0 }}>
                    <InputLabel sx={{ mb: 1.5 }}>{t(`application.field.${name}.label`)}</InputLabel>
                    <div>{value}</div>
                  </Grid>

                  {!lastChild && (
                    <Divider
                      sx={{
                        my: {
                          xs: 1,
                          sm: 0,
                        },
                        mx: {
                          sm: 2,
                          lg: 3,
                        },
                      }}
                      orientation={isMobile ? 'horizontal' : 'vertical'}
                      flexItem
                    />
                  )}
                </Fragment>
              );
            })}
        </Grid>
      </Paper>

      {/* SAA-91 — two tabs now. The idle (`#F9EDF5` -> `primary.light`) style lives on
      `.MuiTab-root`; white bg + no bottom border move to `&.Mui-selected` (a bar-level bgcolor
      would paint both tabs white). The `-1px` root overlap and the scroller fix are untouched:
      MUI's scroller is `overflow: hidden` and clips the overlap if it sits on the Tab instead. */}
      <Tabs
        value={tab}
        onChange={(_event, value: number) => setTab(value)}
        aria-label={t('user.page.single.subtitle') ?? undefined}
        sx={{
          minHeight: '52px',
          width: 'fit-content',
          position: 'relative',
          zIndex: 1,
          mb: '-1px',
          '& .MuiTabs-scroller': { overflow: 'visible !important' },
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            minHeight: '52px',
            textTransform: 'none',
            fontWeight: 400,
            color: 'text.primary',
            bgcolor: 'primary.light',
            border: '1px solid',
            borderColor: 'grey.500',
            // `borderRightWidth`, not `borderRight` — the shorthand resets the colour to
            // `currentColor` and repaints that edge in the text colour.
            borderRadius: 0,
            borderRightWidth: 0,
            '&:first-of-type': { borderTopLeftRadius: '16px' },
            '&:last-of-type': { borderTopRightRadius: '16px', borderRightWidth: '1px' },
            // Design's `16px 24px` (§3 Q3b, ruled at the gate) — one shared rule, so the
            // Application tab's width moves too, deliberately, not as a side effect of tab #2.
            padding: '16px 24px',
            gap: 1,
            '& .MuiTab-iconWrapper': { fontSize: 20, m: 0 },
            // Without this MUI paints the selected tab (label and icon) `primary.main`.
            '&.Mui-selected': {
              color: 'text.primary',
              fontWeight: 600,
              bgcolor: 'background.paper',
              borderBottom: 'none',
            },
          },
        }}
      >
        <Tab
          id="application-tab-0"
          aria-controls="application-tabpanel-0"
          icon={<ApplicationTabIcon />}
          iconPosition="start"
          label={t('application.tab.application')}
        />
        <Tab
          id="application-tab-1"
          aria-controls="application-tabpanel-1"
          icon={<WatchlistTabIcon />}
          iconPosition="start"
          label={<WatchlistTabLabel count={watchlistViewed ? 0 : watchlist?.items?.length ?? 0} />}
        />
      </Tabs>

      <Paper
        sx={{
          borderRadius: '0 16px 16px 16px',
          p: {
            xs: 2,
            sm: 3,
            lg: 5,
          },
          mb: 5,
        }}
        variant="outlined"
        elevation={0}
      >
        {isOpen && <UpdatePdfCoverModal id={id} open={isOpen} onClose={close} />}

        <div role="tabpanel" id="application-tabpanel-0" aria-labelledby="application-tab-0" hidden={tab !== 0}>
          {tab === 0 && (
            <>
              {/* Title, subtitle and actions share the ring's row, so they are passed down. */}
              {data?.applicationGetOne && (
                <ApplicationList
                  item={data.applicationGetOne}
                  onClick={handleSurveyClick}
                  isAdmin
                  actions={
                    <>
                      <ExportPdfDropdown
                        applicationId={id}
                        label={t('application.button.exportPdf.name')}
                        arrow
                        sx={{ height: 36 }}
                      />

                      {/* SAA-112 design pass — "Actions" (ISC.Button contained/primaryLight) replaces the
                      standalone "Update profile highlights" button; "Initiate a new journey" joins it. */}
                      <Dropdown
                        label={t('application.button.actions.name')}
                        color="primaryLight"
                        arrow
                        popperWidth={340}
                        popperPlacement="bottom-end"
                        popperOffset={8}
                        sx={{ minWidth: 120, height: 36 }}
                        // SAA-181 (D5) — was a hardcoded ISC-pink hex; tokenised so WCS gets its own shade.
                        activeSx={{ bgcolor: 'primaryLight.dark', '&:hover': { bgcolor: 'primaryLight.dark' } }}
                        paperSx={{
                          py: 0,
                          my: 0,
                          border: '1px solid #EEF0F3',
                          boxShadow: '0 4px 16px rgba(51,51,51,0.16)',
                          overflow: 'hidden',
                        }}
                        itemSx={{
                          justifyContent: 'flex-start',
                          py: 2,
                          px: 2.5,
                          my: 0,
                          borderRadius: 0,
                          width: '100%',
                          fontSize: 16,
                          fontWeight: 400,
                          color: 'text.primary',
                          textAlign: 'left',
                          borderBottom: '1px solid #EEF0F3',
                          '&:last-of-type': { borderBottom: 'none' },
                          '&:hover': { bgcolor: '#F7F8F9' },
                        }}
                        options={[
                          { id: 'highlights', name: t('application.button.updateProfilePdf.name'), onClick: show },
                          { id: 'newJourney', name: t('application.button.newApp.name'), onClick: handleNewApp },
                          // AC-3 — absent while the profile is still editable by status alone.
                          ...(data?.applicationGetOne?.status && !isEditableStatus(data.applicationGetOne.status)
                            ? [
                                {
                                  id: 'unlockLock',
                                  name: t(
                                    isUnlocked
                                      ? 'application.button.lockProfile.name'
                                      : 'application.button.unlockProfile.name',
                                  ),
                                  onClick: handleUnlockLock,
                                },
                              ]
                            : []),
                        ]}
                      />
                    </>
                  }
                />
              )}

              <Box mt={4}>
                <Paper
                  sx={{
                    p: {
                      xs: 2,
                      sm: 3,
                      lg: 5,
                    },
                    borderRadius: '16px',
                  }}
                  variant="outlined"
                  elevation={0}
                >
                  <Typography variant="h4" mb={3}>
                    {t('application.field.photos.subtitle')}
                  </Typography>

                  {data?.applicationGetOne && <UploadForm applicationId={id} />}
                </Paper>
              </Box>
            </>
          )}
        </div>

        <div role="tabpanel" id="application-tabpanel-1" aria-labelledby="application-tab-1" hidden={tab !== 1}>
          {tab === 1 && <WatchlistPanel watchlist={watchlist} error={watchlistError} />}
        </div>
      </Paper>
      <Loader isLoading={loading || watchlistLoading} />
    </>
  );
};
